/**
 * IconFilePickerDialog — pick an image from Vault Documents to use as
 * an arche icon. Wraps `VaultFilePickerDialog`, downloads the bytes,
 * and hands the parent a `File` blob ready for the icon upload pipeline.
 */

import { useCallback } from 'react';
import { toast } from '@/components/ui/use-toast';
import { VaultFilePickerDialog, type VaultPickerResult } from '@/components/VaultFilePickerDialog';

const ALLOWED_ICON_MIME_TYPES = new Set(['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml']);

export interface IconFilePickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onFileSelected: (file: File) => void;
}

export function IconFilePickerDialog({
  open,
  onOpenChange,
  onFileSelected,
}: IconFilePickerDialogProps) {
  const handlePicked = useCallback(
    async (picked: VaultPickerResult) => {
      const mime = picked.mimeType ?? '';
      if (!ALLOWED_ICON_MIME_TYPES.has(mime)) {
        toast({
          title: 'Unsupported icon format',
          description: `"${picked.fileName}" isn't a supported image type. Choose a PNG, JPEG, WebP, or SVG file.`,
          variant: 'destructive',
        });
        return;
      }
      try {
        const response = await fetch(picked.downloadUrl);
        if (!response.ok) throw new Error(`Download failed (${response.status})`);
        const blob = await response.blob();
        const finalMime = mime || blob.type || 'application/octet-stream';
        const file = new File([blob], picked.fileName, { type: finalMime });
        onFileSelected(file);
      } catch (err) {
        toast({
          title: 'Failed to load file',
          description: err instanceof Error ? err.message : 'An error occurred',
          variant: 'destructive',
        });
      }
    },
    [onFileSelected],
  );

  return (
    <VaultFilePickerDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Choose Icon from Fias Files"
      description="Pick a PNG, JPEG, WebP, or SVG file from your Fias storage. The icon must be 1024×1024 for raster formats; sized variants (64/128/256/512 px) are generated automatically."
      mimeFilter={(doc) => ALLOWED_ICON_MIME_TYPES.has(doc.mimeType ?? '')}
      uploadAccept="image/png,image/jpeg,image/webp,image/svg+xml"
      confirmLabel="Use as Icon"
      onFileSelected={handlePicked}
    />
  );
}

export default IconFilePickerDialog;
